import axios from "axios"
import api from "@/store/api";
import router from "@/router";
import { languages } from "@/store/language";
import DeleteUndoToast from "@/components/DeleteUndoToast.vue";

export const state = {
  data: { year: '', month: '', week: ''},
  selectedAppointment: {},
  searchResults: [],
  loading: false,
  allDays: [],
}

export const mutations = {
  SET_PROGRAMARI (state, data) {
    state.data = data
  },
  SET_ALL_DAYS (state, days) {
    state.allDays = days
  },
  SET_SELECTED_APPOINTMENT (state, data) {
    state.selectedAppointment = JSON.parse(JSON.stringify(data));
  },
  SET_SELECTED_APPOINTMENT_FIELD (state, {key, value}) {
    state.selectedAppointment[key] = value;
  },
  UPDATE_EDITED_APPOINTMENT(state, updatedItem) {
    const newDays = [...state.data.days];
    let removeItem = +state.data.month !== +updatedItem.month;
    let movedItem = null;

    // Find and remove item from its current day
    newDays.forEach(day => {
      const index = day.items.findIndex(item => item.id === updatedItem.id);
      if (index > -1) {
        if (removeItem) {
          day.items = day.items.filter(item => item.id !== updatedItem.id);
        } else {
          movedItem = { ...updatedItem };
          day.items = day.items.filter(item => item.id !== updatedItem.id);
        }
      }
    });

    // If item was moved to a new day in the same month
    if (movedItem) {
      let targetDay = newDays.find(day => day.date === updatedItem.day);
      if (!targetDay) {
        targetDay = {
          date: updatedItem.day,
          day: updatedItem.day,
          items: []
        };
        newDays.push(targetDay);
      }
      targetDay.items = [...targetDay.items, movedItem];
    }

    // Filter empty days and sort by date
    state.data.days = newDays
      .filter(day => day.items.length > 0)
      .sort((a, b) => a.date - b.date);

    // allDays (source of MobileSchedule's list + week-strip dots) spans
    // prev/current/next month and was never touched here, so edits didn't
    // show up on mobile without a manual reload — keep it in sync too.
    const newAllDays = state.allDays.map(day => ({
      ...day,
      items: day.items.filter(item => item.id !== updatedItem.id)
    }));
    let targetAllDay = newAllDays.find(day => day.date === updatedItem.day && +day.month === +updatedItem.month);
    if (!targetAllDay) {
      targetAllDay = { date: updatedItem.day, day: updatedItem.day, month: +updatedItem.month, items: [] };
      newAllDays.push(targetAllDay);
    }
    targetAllDay.items = [...targetAllDay.items, { ...updatedItem }];
    state.allDays = newAllDays.filter(day => day.items.length > 0);
  },
  REMOVE_BOOKING (state, targetId) {
    // Loop through days
    for (let dayIndex = 0; dayIndex < state.data.days.length; dayIndex++) {
      const day = state.data.days[dayIndex];

      // Loop through items in each day
      const itemIndex = day.items.findIndex(item => item.id === targetId);

      if (itemIndex !== -1) {
        // Remove the item
        day.items.splice(itemIndex, 1);

        // If day has no more items, remove the day
        if (day.items.length === 0) {
          state.data.days.splice(dayIndex, 1);
        }

        break; // found and removed from data.days — still need to clean allDays below
      }
    }

    // Same allDays sync issue as UPDATE_EDITED_APPOINTMENT above.
    state.allDays = state.allDays
      .map(day => ({ ...day, items: day.items.filter(item => item.id !== targetId) }))
      .filter(day => day.items.length > 0);
  },
  // Undo for delete_appointment — re-inserts a snapshot taken right before
  // REMOVE_BOOKING ran. Mirrors UPDATE_EDITED_APPOINTMENT's "create the day
  // bucket if it's missing" handling for both day collections.
  RESTORE_BOOKING (state, item) {
    if (+state.data.month === +item.month) {
      const days = [...state.data.days];
      let targetDay = days.find(day => day.date === item.day);
      if (!targetDay) {
        targetDay = { date: item.day, day: item.day, items: [] };
        days.push(targetDay);
      }
      targetDay.items = [...targetDay.items, item];
      state.data.days = days.sort((a, b) => a.date - b.date);
    }

    const allDays = [...state.allDays];
    let targetAllDay = allDays.find(day => day.date === item.day && +day.month === +item.month);
    if (!targetAllDay) {
      targetAllDay = { date: item.day, day: item.day, month: +item.month, items: [] };
      allDays.push(targetAllDay);
    }
    targetAllDay.items = [...targetAllDay.items, item];
    state.allDays = allDays;
  },
  SET_SEARCH_RESULTS (state, data) {
    state.searchResults = data
  },
  SET_LOADING (state, value) {
     state.loading = value
  }
}

export const actions = {
  async create_programare ({ dispatch }, payload) {
    await axios.post(api.programari, payload);

    // Re-fetch the booked month/year from the server instead of trusting the
    // POST response shape — it's the create endpoint's response, not the
    // {year, month, days} shape SET_PROGRAMARI expects, so committing it
    // directly here would corrupt state.data (and never touched state.allDays
    // at all, so the week-strip dots/list stayed stale after creating).
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(payload.booking_date || '');
    const year = m ? +m[1] : new Date().getFullYear();
    const month = m ? +m[2] - 1 : new Date().getMonth();
    await dispatch('fetch_programari', `?month=${month}&year=${year}`);

    await router.push({ path: '/programari', query: { date: payload.booking_date } });
  },
  async fetch_programari ({ commit }, query = '') {
    const params = new URLSearchParams(query.replace('?', ''));
    const month = params.has('month') ? +params.get('month') : new Date().getMonth();
    const year = params.has('year') ? +params.get('year') : new Date().getFullYear();

    try {
      commit('SET_PROGRAMARI', {});
      commit('SET_ALL_DAYS', []);

      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;

      const [current, prev, next] = await Promise.all([
        axios.get(`${api.programari}?month=${month}&year=${year}`),
        axios.get(`${api.programari}?month=${prevMonth}&year=${prevYear}`),
        axios.get(`${api.programari}?month=${nextMonth}&year=${nextYear}`),
      ]);

      // Don't rely on the backend echoing month/year back in the response —
      // we already know them from the request params, so set them ourselves
      // to guarantee data.month/data.year are always defined after a
      // successful fetch (this is what UI loading states key off of).
      commit('SET_PROGRAMARI', { ...current.data, month, year });

      const allDays = [
        ...(prev.data.days || []).map(d => ({ ...d, month: prevMonth })),
        ...(current.data.days || []).map(d => ({ ...d, month })),
        ...(next.data.days || []).map(d => ({ ...d, month: nextMonth })),
      ];
      commit('SET_ALL_DAYS', allDays);

    } catch (err) {
      console.log(`ERROR get programari`)
      // Fall back to an empty-but-defined month so UI loading states (e.g.
      // the mobile skeleton, gated on data.month/year being set) don't get
      // stuck forever when the request fails.
      commit('SET_PROGRAMARI', { year, month, week: '', days: [] });
    }
  },
  async update_appointment ({ state, commit }, item) {
    try {
      const url = `${api.programari}/${item.id}`;
      const { data } = await axios.put(url, state.selectedAppointment);
      commit('SET_MODAL', false);
      // todo create a mutation to find and update the specific edited object details
      // commit('SET_PROGRAMARI', data);
      commit('UPDATE_EDITED_APPOINTMENT', data);
    } catch (err) {
      console.log(`ERROR update appointment`)
    }
  },
  // Optimistic: removes locally and closes the view/edit modal right away,
  // then shows an undo-capable toast and only fires the real DELETE once
  // that toast closes (timeout or manual dismiss) without Undo having been
  // clicked. Undo just skips the network call and restores the snapshot —
  // nothing was actually deleted server-side yet, so there's nothing to
  // reverse there.
  async delete_appointment ({ commit, state, rootState }, id) {
    let snapshot = null;
    for (const day of state.allDays) {
      const found = day.items.find(item => item.id === id);
      if (found) { snapshot = { ...found }; break; }
    }

    commit('REMOVE_BOOKING', id);
    commit('SET_MODAL', false);

    const lang = languages[rootState.lang] || languages.ro;
    let undone = false;

    this._vm.$toast.success(
      {
        component: DeleteUndoToast,
        props: {
          message: lang.programariSchedule.deleteSuccessToast,
          undoLabel: lang.programariSchedule.deleteConfirmCancel
        },
        listeners: {
          undo: () => {
            undone = true;
            if (snapshot) commit('RESTORE_BOOKING', snapshot);
          }
        }
      },
      {
        timeout: 5000,
        onClose: async () => {
          if (undone || !snapshot) return;
          try {
            await axios.delete(`${api.programari}/${id}`);
          } catch (e) {
            // The optimistic removal is now wrong — the record is still
            // there server-side, so bring it back locally too.
            commit('RESTORE_BOOKING', snapshot);
            this._vm.$toast.error(lang.programariSchedule.deleteError);
          }
        }
      }
    );
  },
  async search_programari ({ commit }, query) {
    try {
      commit('SET_LOADING', true)
      // Same grouped-by-day calendar shape as the regular /api/bookings?month&year
      // response ({ year, month, days: [{ date, day, items: [...] }] }) — flatten
      // it into the flat item list the search UI renders.
      const { data } = await axios.get(`${api.programari}?search=${query}`);

      const months = [
        "Ian.", "Feb.", "Mart.", "Apr.", "Mai", "Iun.",
        "Iul.", "Aug.", "Sept.", "Oct.", "Nov.", "Dec."
      ];

      const items = (data.days || []).flatMap(day => day.items || []);
      const formatted = items.map(item => ({
        ...item,
        date: `${item.day} ${months[item.month]} ${item.year}`
      }));

      commit('SET_SEARCH_RESULTS', formatted);
    } catch (err) {
      console.log(`ERROR search programari`, err.message)
    } finally {
      setTimeout(() => {
        commit('SET_LOADING', false)
      }, 1000)
    }
  }
}

export const getters = {
  // Single source for week view, month view chips and the day-sheet modal —
  // sorting here (rather than in each consumer) fixes chronological order
  // everywhere at once, instead of the server's returned order.
  appointmentsByDate: state => {
    return state.allDays.reduce((acc, day) => {
      acc[`${day.month}-${day.date}`] = [...day.items].sort((a, b) => {
        return (+a.hours - +b.hours) || (+a.minutes - +b.minutes);
      });
      return acc;
    }, {});
  }
}